// helpers/handleDrawerClose.js
import PropTypes from 'prop-types'
import { Logger } from '../logger'
import isEqual from 'lodash/isEqual'
import { getLineProductId } from '../utils/itemHelpers'
/**
 * Generic close handler for item drawers (calibrations, services, parts, labour).
 * Works for both workorders and quotes by accepting `type` and `sourceData`.
 *
 * @param {Object} params - The parameters.
 * @param {Function} [params.setLoading] - Optional setter for loading state.
 * @param {Function} params.setDrawerOpen - Setter to close the drawer.
 * @param {Function} params.updateFn - API function to update item list.
 * @param {Function} params.setItems - Setter for local items state.
 * @param {Object} params.sourceData - The quote or work order object.
 * @param {Array} [params.data] - The ticked drawer items (`id` is the product id). Undefined when the drawer had no data: nothing is saved.
 * @param {String} params.logFile - The file name for logging.
 * @param {String} params.itemKey - The API payload key (e.g., 'calibrations', 'services').
 * @param {String} params.type - Either 'workorders' or 'quotes'.
 */

// Product type of the lines held under each `itemKey` of the quote / work order.
const PRODUCT_TYPE_BY_KEY = {
  calibrations: 'calibration',
  services: 'service',
  parts: 'part',
  labors: 'labour'
}

// Sorted distinct product ids. Duplicate lines of one product (Auto Quote tiers) and renamed
// products compare equal, so an unchanged drawer never triggers a save.
const toIdSet = (ids = []) => [...new Set(ids.filter((id) => id !== undefined && id !== null).map(String))].sort()

export const handleDrawerClose = async ({
  setLoading = () => {},
  setDrawerOpen,
  updateFn,
  setItems,
  sourceData,
  data,
  logFile,
  itemKey,
  type,
  onSuccess
}) => {
  try {
    setDrawerOpen(false)

    // No drawer data (catalog not loaded): never save — the update endpoints replace the whole list.
    if (!Array.isArray(data)) return

    const productType = PRODUCT_TYPE_BY_KEY[itemKey]
    const original = sourceData?.[itemKey] || []
    const originalIds = toIdSet(original.map((line) => getLineProductId(line, productType)))
    const newIds = toIdSet(data.map((item) => item?.id))

    if (isEqual(originalIds, newIds)) return

    setLoading(true)

    const payload = {
      type,
      id: sourceData.id,
      [itemKey]: data
    }

    const response = await updateFn(payload)
    if (response?.status === 200) {
      setItems(data)
      if (onSuccess) onSuccess()
    }
  } catch (error) {
    Logger.error({
      message: error,
      payload: {
        file: `${type === 'quotes' ? 'QuoteSummaryPage' : 'WorkOrderSummaryPage'}/${logFile}`,
        id: sourceData?.id
      }
    })
  } finally {
    setLoading(false)
  }
}

handleDrawerClose.propTypes = {
  setLoading: PropTypes.func,
  setDrawerOpen: PropTypes.func.isRequired,
  updateFn: PropTypes.func.isRequired,
  setItems: PropTypes.func.isRequired,
  sourceData: PropTypes.object.isRequired,
  data: PropTypes.array,
  logFile: PropTypes.string.isRequired,
  itemKey: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['workorders', 'quotes']).isRequired
}
